import { z } from 'zod';
import { CHAIN_QUORUMS, CHAIN_STEP_KINDS } from '../forms/approval-chain.js';
import { DOCUMENT_KINDS, DOCUMENT_SCOPES, DOCUMENT_SECTIONS } from './library.js';
import { READ_AUDIENCES } from './reading.js';

/**
 * Doküman kütüphanesinin ortak şemaları (CLAUDE.md "Form ve doğrulama"): gemi, bulut ve arayüz
 * aynı şemayı kullanır. Hata iletileri arayüzde `documents.errors.<kod>` ile çevrilir.
 */

const text = (min: number, max: number) => z.string().trim().min(min).max(max);

/** C0 (U+0000–U+001F), DEL ve C1 (U+007F–U+009F) kontrol karakteri var mı. */
function hasControlCharacter(value: string): boolean {
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) return true;
  }
  return false;
}

/**
 * Tek satırlık alan (doküman no, başlık, revizyon no, klasör kodu ve adı): kontrol karakteri
 * yok. Bu değerler baskı altlığına (CSS dizesi), indirilen dosya adına ve pakete girer; CR ya
 * da FF altlığın CSS dizesini kırıp filigranı gizleyebilirdi (inceleme SEC-2; ADR 0028 #30).
 */
const line = (min: number, max: number) =>
  text(min, max).refine((value) => !hasControlCharacter(value), { message: 'CONTROL_CHARACTER' });
const sortOrder = z.number().int().min(0).max(1_000_000).default(0);

export const folderInputSchema = z.strictObject({
  parentId: z.uuid().nullable(),
  section: z.enum(DOCUMENT_SECTIONS),
  code: line(1, 32),
  name: line(1, 120),
  sortOrder,
});
export type FolderInput = z.infer<typeof folderInputSchema>;

/**
 * Formun onay zinciri ayarı (ADR 0071 §2): biçim burada; sıra, sayılar ve rollerin şirkette olması
 * yazma yolunda (`parseApprovalChain`). Boş (`null`) onay matrisinin kuralıdır.
 */
export const approvalChainSchema = z.strictObject({
  steps: z
    .array(
      z.strictObject({
        kind: z.enum(CHAIN_STEP_KINDS),
        roleIds: z.array(z.uuid()).min(1).max(2),
        quorum: z.enum(CHAIN_QUORUMS),
      }),
    )
    .min(2)
    .max(5),
});

export const documentInputSchema = z
  .strictObject({
    folderId: z.uuid(),
    docNo: line(1, 32),
    title: line(1, 200),
    kind: z.enum(DOCUMENT_KINDS),
    description: text(1, 2000).nullable().default(null),
    scope: z.enum(DOCUMENT_SCOPES),
    scopeVesselIds: z.array(z.uuid()).max(500).default([]),
    sortOrder,
    /** Kişisel okumanın kitlesi (ADR 0028 #29). */
    readAudience: z.enum(READ_AUDIENCES).default('all_vessel_roles'),
    readRoleIds: z.array(z.uuid()).max(200).default([]),
    /** Formun onay zinciri (ADR 0071 §2); yalnız `form` türünde. */
    approvalChain: approvalChainSchema.nullable().default(null),
  })
  .superRefine((value, ctx) => {
    if (value.approvalChain !== null && value.kind !== 'form') {
      ctx.addIssue({ code: 'custom', path: ['approvalChain'], message: 'CHAIN_ONLY_FOR_FORMS' });
    }
    // Kapsam "seçili gemiler" ise liste zorunlu; diğerlerinde liste anlamsızdır
    const needs = value.scope === 'vessels';
    if (needs !== value.scopeVesselIds.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['scopeVesselIds'], message: 'SCOPE_VESSELS' });
    }
    if (new Set(value.scopeVesselIds).size !== value.scopeVesselIds.length) {
      ctx.addIssue({ code: 'custom', path: ['scopeVesselIds'], message: 'DUPLICATE' });
    }
    // Seçili roller kitlesinde rol listesi zorunlu; diğerinde anlamsız
    if ((value.readAudience === 'roles') !== value.readRoleIds.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['readRoleIds'], message: 'READ_ROLES' });
    }
    if (new Set(value.readRoleIds).size !== value.readRoleIds.length) {
      ctx.addIssue({ code: 'custom', path: ['readRoleIds'], message: 'DUPLICATE' });
    }
  });
export type DocumentInput = z.infer<typeof documentInputSchema>;
/** Şemaya verilen ham girdi: varsayılanı olan alanlar (kitle, roller, açıklama) verilmeyebilir. */
export type DocumentInputData = z.input<typeof documentInputSchema>;

/**
 * Gemi belgesi (ADR 0036, ADR 0038 §12): klasör geminin ağacından; kapsam, okuma rolü listesi ve
 * ilişki yoktur (kitle her zaman geminin bütün rolleri). Fazla alan `VALIDATION_FAILED`.
 */
export const vesselDocumentInputSchema = z.strictObject({
  folderId: z.uuid(),
  docNo: line(1, 32),
  title: line(1, 200),
  kind: z.enum(DOCUMENT_KINDS),
  description: text(1, 2000).nullable().default(null),
  sortOrder,
});
export type VesselDocumentInput = z.infer<typeof vesselDocumentInputSchema>;
export type VesselDocumentInputData = z.input<typeof vesselDocumentInputSchema>;

/** Yeni revizyon (taslak): şirketin revizyon numarası ve zorunlu değişiklik özeti (§3.2). */
export const revisionInputSchema = z.strictObject({
  revisionNo: line(1, 32),
  changeSummary: text(1, 2000),
  /** Kişisel okuma gerekir mi; verilmezse türün varsayılanı (ADR 0028 #29). */
  readRequired: z.boolean().optional(),
});
export type RevisionInput = z.infer<typeof revisionInputSchema>;
